import React, { useEffect, useState } from 'react';
import { Link, useLocation, matchPath } from 'react-router-dom';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { cn } from '../../utils';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import {
  Sheet,
  SheetContent,
  ScrollArea,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/shadcn';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose, children }) => {
  const isLg = useMediaQuery('(min-width: 1024px)');

  if (isLg) {
    return (
      <aside className="flex h-full w-64 shrink-0 flex-col border-r border-border bg-card text-card-foreground">
        {children}
      </aside>
    );
  }

  return (
    <Sheet open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <SheetContent side="left" className="flex w-[min(100vw,18rem)] max-w-[85vw] flex-col gap-0 border-r p-0">
        {children}
      </SheetContent>
    </Sheet>
  );
};

interface SidebarHeaderProps {
  children: React.ReactNode;
}

const SidebarHeader: React.FC<SidebarHeaderProps> = ({ children }) => {
  return (
    <div className="flex h-16 shrink-0 items-center justify-between border-b border-border px-6">
      {children}
    </div>
  );
};

interface SidebarContentProps {
  children: React.ReactNode;
}

const SidebarContent: React.FC<SidebarContentProps> = ({ children }) => {
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <ScrollArea className="flex-1">
        <nav className="space-y-2 px-4 py-6">{children}</nav>
      </ScrollArea>
    </div>
  );
};

interface SidebarFooterProps {
  children: React.ReactNode;
}

const SidebarFooter: React.FC<SidebarFooterProps> = ({ children }) => {
  return <div className="shrink-0 border-t border-border p-4">{children}</div>;
};

interface SidebarItemProps {
  icon?: React.ReactNode;
  label: string;
  href?: string;
  isActive?: boolean;
  onClick?: () => void;
  children?: React.ReactNode;
}

const SidebarItem: React.FC<SidebarItemProps> = ({
  icon,
  label,
  href,
  isActive = false,
  onClick,
  children,
}) => {
  const content = (
    <div
      className={cn(
        'flex cursor-pointer items-center space-x-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors hover:bg-accent hover:text-accent-foreground',
        isActive && 'bg-accent text-accent-foreground',
      )}
      onClick={onClick}
    >
      {icon && <span className="flex-shrink-0">{icon}</span>}
      <span className="flex-1">{label}</span>
      {children}
    </div>
  );

  if (href) {
    return (
      <a href={href} className="block cursor-pointer">
        {content}
      </a>
    );
  }

  return content;
};

interface SidebarGroupProps {
  title?: string;
  children: React.ReactNode;
}

const SidebarGroup: React.FC<SidebarGroupProps> = ({ title, children }) => {
  return (
    <div className="space-y-1">
      {title && (
        <h3 className="px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          {title}
        </h3>
      )}
      <div className="space-y-1">{children}</div>
    </div>
  );
};

interface SidebarExpandableItemProps {
  icon?: React.ReactNode;
  label: string;
  defaultExpanded?: boolean;
  expandWhenActive?: boolean;
  sectionActive?: boolean;
  children: React.ReactNode;
}

const SidebarExpandableItem: React.FC<SidebarExpandableItemProps> = ({
  icon,
  label,
  defaultExpanded = false,
  expandWhenActive = false,
  sectionActive = false,
  children,
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded);

  useEffect(() => {
    if (expandWhenActive) {
      setExpanded(true);
    }
  }, [expandWhenActive]);

  return (
    <Collapsible open={expanded} onOpenChange={setExpanded}>
      <CollapsibleTrigger asChild>
        <button
          type="button"
          className={cn(
            'flex w-full cursor-pointer items-center space-x-3 rounded-lg px-3 py-2 text-left text-sm font-medium transition-colors hover:bg-accent hover:text-accent-foreground',
            sectionActive && 'bg-accent/60 text-accent-foreground',
          )}
        >
          {icon && <span className="flex-shrink-0">{icon}</span>}
          <span className="flex-1">{label}</span>
          {expanded ? (
            <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
          ) : (
            <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
          )}
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="ml-4 space-y-0.5 border-l border-border pl-3 pt-0.5">{children}</div>
      </CollapsibleContent>
    </Collapsible>
  );
};

interface SidebarExternalLinkProps {
  label: string;
  href: string;
  icon?: React.ReactNode;
}

const SidebarExternalLink: React.FC<SidebarExternalLinkProps> = ({ label, href, icon }) => {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={cn(
        'flex cursor-pointer items-center space-x-2 rounded-md px-2 py-1.5 text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground',
      )}
    >
      {icon && <span className="flex-shrink-0">{icon}</span>}
      <span>{label}</span>
    </a>
  );
};

interface SidebarNavLinkProps {
  label: string;
  to: string;
  icon?: React.ReactNode;
  /** Called after navigate (e.g. close mobile sheet). */
  onNavigate?: () => void;
}

const SidebarNavLink: React.FC<SidebarNavLinkProps> = ({ label, to, icon, onNavigate }) => {
  const location = useLocation();
  const isActive = !!matchPath({ path: to, end: false }, location.pathname);

  return (
    <Link
      to={to}
      onClick={() => onNavigate?.()}
      className={cn(
        'flex cursor-pointer items-center space-x-2 rounded-md px-2 py-1.5 text-sm transition-colors hover:bg-accent hover:text-accent-foreground',
        isActive ? 'bg-accent font-medium text-accent-foreground' : 'text-muted-foreground',
      )}
    >
      {icon && <span className="flex-shrink-0">{icon}</span>}
      <span>{label}</span>
    </Link>
  );
};

export {
  Sidebar,
  SidebarHeader,
  SidebarContent,
  SidebarFooter,
  SidebarItem,
  SidebarGroup,
  SidebarExpandableItem,
  SidebarExternalLink,
  SidebarNavLink,
};
